import type { LucideIcon } from 'lucide-react';
import {
  AtSign,
  Bell,
  Building2,
  CalendarCheck,
  ClipboardCheck,
  ClipboardList,
  Clock,
  ListX,
  MessageCircle,
  Shield,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';

export function formatNotificationTimeCompact(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Short list timestamp: now / 5m / 3h / Mon / Oct 3 / Nov 3, 2025. A row slightly in
 * the future (client clock behind the server's) reads as "now" rather than a negative age.
 */
export function formatNotificationTimeRelative(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const age = now.getTime() - date.getTime();
  if (age < MINUTE_MS) return 'now';
  if (age < HOUR_MS) return `${Math.floor(age / MINUTE_MS)}m`;
  if (age < DAY_MS) return `${Math.floor(age / HOUR_MS)}h`;
  if (age < 7 * DAY_MS) return date.toLocaleDateString('en-US', { weekday: 'short' });
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function notificationIconForType(type: string): { Icon: LucideIcon; color: string } {
  switch (type) {
    case 'task_assigned':
      return { Icon: ClipboardList, color: '#007AFF' };
    case 'task_denied':
      return { Icon: ListX, color: '#FF3B30' };
    case 'volunteer_event_accepted':
      return { Icon: CalendarCheck, color: '#34C759' };
    case 'organization_membership_accepted':
      return { Icon: Building2, color: '#5856D6' };
    case 'organization_promoted_admin':
      return { Icon: Shield, color: '#FF9500' };
    case 'event_application_pending':
      return { Icon: UserPlus, color: '#5856D6' };
    case 'volunteer_left_event':
      return { Icon: UserMinus, color: '#FF3B30' };
    case 'organization_membership_pending':
      return { Icon: Building2, color: '#007AFF' };
    case 'task_submission_pending':
      return { Icon: ClipboardCheck, color: '#34C759' };
    case 'hours_request_pending':
      return { Icon: Clock, color: '#FF9500' };
    case 'chat_mention':
      // Orange, not the chat blue: being named is the one chat row worth picking out of
      // a list of them at a glance.
      return { Icon: AtSign, color: '#FF9500' };
    case 'direct_message':
    case 'event_message':
      return { Icon: MessageCircle, color: '#007AFF' };
    case 'department_message':
      return { Icon: Users, color: '#007AFF' };
    default:
      return { Icon: Bell, color: '#8E8E93' };
  }
}
